import { useToast, useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useRateLimitWarning } from "@/hooks/useRateLimitWarning";
import { useTranslation } from "@/hooks/useTranslation";
import { getEmailRevealErrorMessages } from "@/lib/employee-email-reveal";
import { executeWithMinimumTime } from "@/lib/execute-with-minimum-time";
import { EmployeeDTO } from "@shaarp/common";
import { useCallback } from "react";

type RevealEmailOptions = {
  employeeId: string;
  /** Absent for contacts whose company was never resolved (search results). */
  companyId?: string;
  /** Holds the spinner for at least this long, for surfaces that show one. */
  minimumDurationMs?: number;
};

/**
 * Reveals an employee email, with the toast every refusal deserves.
 *
 * Shared rather than duplicated because two surfaces now trigger a reveal — the
 * email cell and the CRM send, which reveals before creating a contact a CRM
 * would refuse without an address — and a throttle or a reached cap has to read
 * the same on both.
 */
export const useEmployeeEmailReveal = () => {
  const api = useApi();
  const { toast } = useToast();
  const toastError = useToastError();
  const { t } = useTranslation();
  const { warnIfRateLimited } = useRateLimitWarning();

  /**
   * Asks the API for the contact's email.
   *
   * @returns The employee as the reveal left it — `email` is null when none was
   *   found, which is an answer, not a failure — or null when the request
   *   itself failed, in which case the user has already been told why.
   */
  const revealEmail = useCallback(
    async ({
      employeeId,
      companyId,
      minimumDurationMs = 0,
    }: RevealEmailOptions): Promise<EmployeeDTO | null> => {
      const request = async () => {
        const { data } = await api.get<EmployeeDTO>(
          `/company/${companyId}/employee/${employeeId}/enrich-email`,
        );
        // The endpoint always answers with a DTO (known outcomes like
        // UNAVAILABLE are statuses, not errors), but an empty body must never
        // crash the caller.
        return data ?? null;
      };

      try {
        return minimumDurationMs > 0
          ? await executeWithMinimumTime(request, minimumDurationMs)
          : await request();
      } catch (error) {
        if (warnIfRateLimited(error)) return null;
        // An email lookup that failed (502) is no answer about the person: the
        // generic error below, and the button stays for another try.
        const messages = getEmailRevealErrorMessages(error);
        if (!messages) {
          toastError();
          return null;
        }
        toast({
          variant: "destructive",
          title: t(messages.title),
          description: t(messages.description),
        });
        return null;
      }
    },
    [api, t, toast, toastError, warnIfRateLimited],
  );

  return { revealEmail };
};
